<template>
  <div class="flex gap-4 pb-8 w-full relative">
    <div class="flex-1 max-w-min">
      <div class="h-full flex justify-center">
        <!-- Ligne de progression -->
        <div
          class="absolute h-full border-l-2 transition-all duration-500"
          :class="getLineClass()"
        ></div>
        <!-- Cercle de l'étape -->
        <div
          class="rounded-full w-12 h-12 p-3 relative flex items-center justify-center transition-all duration-300 z-10"
          :class="getCircleClass()"
        >
          <!-- Animation ping uniquement pour l'étape actuelle (non échouée) -->
          <div
            v-if="isCurrentStep && !isFailed && !isCancelled"
            class="absolute inset-0 rounded-full bg-primary animate-ping opacity-75"
          ></div>

          <!-- Icône d'erreur ou icône standard -->
          <svg
            v-if="isFailed || isCancelled"
            class="w-6 h-6 text-red-600 relative z-20"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <ColorImg
            v-else
            :src="icon"
            class="h-full w-full object-cover relative z-20"
            :class="getIconClass()"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col flex-1">
      <div
        class="text-xl font-bold transition-colors duration-300"
        :class="getTextClass()"
      >
        {{ title }}
      </div>
      <div class="mt-1 text-sm opacity-80">{{ description }}</div>
      <div
        class="mt-2 text-xs transition-colors duration-300"
        :class="getDateClass()"
      >
        <span v-if="date">{{ date }}</span>
        <span
          v-else-if="isFailed || isCancelled"
          class="text-red-500 font-semibold"
          >Échoué / Annulé</span
        >
        <span v-else-if="isCurrentStep" class="text-primary font-semibold"
          >En cours...</span
        >
        <span v-else>En attente</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  icon: String,
  title: String,
  description: String,
  date: String,
  active: Boolean,
  isFailed: Boolean,
  isCancelled: Boolean,
  stepName: String,
  allSteps: Array,
  timeline: Object
});

const isCurrentStep = computed(() => {
  if (!props.active || props.isFailed || props.isCancelled) return false;

  const currentIndex = props.allSteps?.indexOf(props.stepName);
  if (currentIndex === -1) return false;

  if (currentIndex < props.allSteps.length - 1) {
    const nextStep = props.allSteps[currentIndex + 1];
    return (
      !props.timeline ||
      !props.timeline[nextStep] ||
      props.timeline[nextStep] === 0 ||
      props.timeline[nextStep] === null
    );
  }

  return props.stepName === 'delivered' ? false : !props.date;
});

const isNextStep = computed(() => {
  if (props.active || props.isFailed || props.isCancelled) return false;

  const stepIndex = props.allSteps?.indexOf(props.stepName);
  if (stepIndex === -1 || stepIndex === 0) return false;

  const prevStep = props.allSteps[stepIndex - 1];
  return !!props.timeline?.[prevStep] || false;
});

const getLineClass = () => {
  if (props.isFailed || props.isCancelled) {
    return 'border-red-300 bg-red-50';
  }
  if (props.active) {
    return 'border-primary bg-primary/10';
  }
  return 'border-primary-100 bg-primary-5';
};

const getCircleClass = () => {
  if (props.isFailed || props.isCancelled) {
    return 'bg-red-100 border-2 border-red-500 shadow-md shadow-red-200';
  }
  if (isCurrentStep.value) {
    return 'bg-primary shadow-lg shadow-primary/30 scale-110';
  } else if (props.active) {
    return 'bg-primary';
  } else if (isNextStep.value) {
    return 'bg-white border-2 border-primary/30 shadow-md';
  }
  return 'bg-primary-100';
};

const getIconClass = () => {
  if (props.active) {
    return 'bg-white';
  } else if (isNextStep.value) {
    return 'bg-primary opacity-60';
  }
  return 'bg-primary opacity-30';
};

const getTextClass = () => {
  if (props.isFailed || props.isCancelled) {
    return 'text-red-600 font-bold';
  }
  if (isCurrentStep.value) {
    return 'text-primary font-bold';
  } else if (props.active) {
    return 'text-primary';
  } else if (isNextStep.value) {
    return 'title-color opacity-90 font-semibold';
  }
  return 'title-color opacity-60';
};

const getDateClass = () => {
  if (props.isFailed || props.isCancelled) {
    return 'text-red-500 font-semibold';
  }
  if (isCurrentStep.value) {
    return 'text-primary font-semibold';
  } else if (props.active) {
    return 'title-color';
  } else if (isNextStep.value) {
    return 'title-color opacity-80';
  }
  return 'title-color opacity-50';
};
</script>
